/*
---------------------------------------------------------
Copyright © 2026 Daniel Woik (DW E-Commerce). All rights reserved.
Custom-coded by DW E-Commerce. This code is licensed for use on
this store only, as part of the DW E-Commerce optimization service.
Copying, reselling, redistributing or using it on any other store
is not permitted without written permission.
---------------------------------------------------------
DW Kit:   Product Card (scroller)
Version:  1.1.0
Updated:  2026-10-13 — Slider rebuilt on Horizon's mechanics; DW line orders, stars from metafields or app code, card and image design settings, image swipe toggle.
Source:   DW-Component-Library/Kits/Product-Card

FUNCTION LOCKED. Load before assets/dw-slider.js.
window.DWScroller: controls one horizontal scroll container.
Same mechanics as the slideshow of Shopify's Horizon theme:
  - to(slide | px) and by(px) scroll smoothly or instantly,
  - it tells user scrolling apart from scrolling started by script,
  - it reports scroll start and scroll end (50 ms without scroll events),
  - "finished" resolves when the running scroll has ended,
  - "snap" switches CSS scroll snapping off while dragging with the mouse.
---------------------------------------------------------
*/
(function () {
  if (window.DWScroller) return;

  var END_DELAY = 50;

  function debounce(fn, wait) {
    var timer;
    var run = function () {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(null, args); }, wait);
    };
    run.cancel = function () { clearTimeout(timer); };
    return run;
  }

  function throttle(fn, wait) {
    var last = 0;
    var timer;
    return function () {
      var args = arguments;
      var now = Date.now();
      var rest = wait - (now - last);
      if (rest <= 0) {
        last = now;
        fn.apply(null, args);
      } else {
        clearTimeout(timer);
        timer = setTimeout(function () { last = Date.now(); fn.apply(null, args); }, rest);
      }
    };
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function Scroller(element, options) {
    var self = this;
    this.element = element;
    this._onScrollStart = options.onScrollStart;
    this._onScrollEnd = options.onScrollEnd;
    this._userCallback = throttle(options.onScroll, END_DELAY);
    this._endCallback = options.onScroll;
    this._promise = null;
    this._resolve = null;
    this._userEvent = true;
    this._ignore = false;
    this._isScrolling = false;

    this._handleScrollEnd = debounce(function (userEvent) {
      if (self._resolve) self._resolve();
      if (userEvent) self._endCallback();
      self._reset();
      if (self._isScrolling) {
        self._isScrolling = false;
        if (self._onScrollEnd) self._onScrollEnd();
      }
    }, END_DELAY);

    this._handleScroll = function () {
      if (!self._isScrolling) {
        self._isScrolling = true;
        if (self._onScrollStart) self._onScrollStart();
      }
      if (self._ignore) {
        self._reset();
        self._handleScrollEnd(false);
        return;
      }
      var userEvent = self._userEvent;
      self._setup();
      if (userEvent) self._userCallback();
      self._handleScrollEnd(userEvent);
    };

    element.addEventListener('scroll', this._handleScroll, { passive: true });
  }

  Scroller.prototype._setup = function () {
    var self = this;
    if (this._promise) return;
    this._promise = new Promise(function (resolve) { self._resolve = resolve; });
  };

  Scroller.prototype._reset = function () {
    this._handleScrollEnd.cancel();
    if (this._resolve) this._resolve();
    this._promise = null;
    this._resolve = null;
    this._userEvent = true;
    this._ignore = false;
  };

  Scroller.prototype._scroll = function (method, value, instant) {
    if (instant === undefined) instant = reducedMotion();
    this._reset();
    this._ignore = instant;
    this._userEvent = false;
    var current = this.element.scrollLeft;
    var target = method === 'scrollBy' ? current + value : value;
    if (Math.abs(target - current) < 1) return;
    if (!instant) this._setup();
    this.element[method]({ left: value, behavior: instant ? 'instant' : 'smooth' });
  };

  /* Scroll to a slide (its left edge, minus the track's start padding) or to a pixel position. */
  Scroller.prototype.to = function (input, options) {
    var instant = options && options.instant;
    var value = input;
    if (input instanceof HTMLElement) {
      var paddingStart = parseFloat(getComputedStyle(this.element).paddingInlineStart) || 0;
      value = input.offsetLeft - paddingStart;
    }
    var max = this.element.scrollWidth - this.element.clientWidth;
    value = Math.max(0, Math.min(value, max));
    if (this.element.scrollLeft !== value) {
      this._scroll('scrollTo', value, instant);
    } else if (this._isScrolling) {
      /* Released at the same position: no scroll event follows, so end it here. */
      this._handleScrollEnd(false);
    }
  };

  Scroller.prototype.by = function (value, options) {
    this._scroll('scrollBy', value, options && options.instant);
  };

  Object.defineProperty(Scroller.prototype, 'finished', {
    get: function () { return this._promise || Promise.resolve(); }
  });

  Object.defineProperty(Scroller.prototype, 'snap', {
    set: function (value) {
      /* Changing the snap type fires a scroll event that is not the user's. */
      this._ignore = true;
      this.element.style.setProperty('scroll-snap-type', value ? 'x mandatory' : 'none');
    }
  });

  Scroller.prototype.destroy = function () {
    this.element.removeEventListener('scroll', this._handleScroll);
  };

  window.DWScroller = Scroller;
})();
